import { Checkbox, Radio } from '@tableau/tableau-ui';
import * as React from 'react';
import * as TypeStyle from 'typestyle';
import { ClickBustingConfig } from '../../config/ClickBusting';
import { TabStyles } from '../../styles/TabStyles';
import { DisplayFlex } from '../../styles/Flexbox';
import { InputAttrs, LabelAttrs } from '../../utils/HtmlAttrs';
import { EventUtilsOptOut } from '../../utils/ReactHelpers';
import { CheckBoxProps } from './CheckBoxWidget';
import { CheckBoxWithLabelProps } from './CheckBoxWithLabelWidget';
import { RadioButtonProps } from './RadioButtonWidget';
import { RadioButtonWithLabelProps } from './RadioButtonWithLabelWidget';

export { CheckBoxProps, CheckBoxWithLabelProps, RadioButtonProps, RadioButtonWithLabelProps };

const StyleName = TypeStyle.style({
  boxSizing: 'border-box',
});

const WithLabelStyleName = TypeStyle.style({
  ...DisplayFlex,
});

const LeftMarginStyleName = TypeStyle.style({
  marginLeft: TabStyles.Spacing.DefaultSpace,
});

type SelectorProps = {
  componentType: typeof Radio | typeof Checkbox;
  removeLeftMargin?: boolean;
  testId: string;
};

const SelectorShim: React.FC<SelectorProps & InputAttrs> = props => {
  const { componentType: Component, removeLeftMargin, testId, ...inputProps } = props;

  return (
    <Component
      {...inputProps}
      className={TypeStyle.classes(
        StyleName,
        !!inputProps.children && WithLabelStyleName,
        !removeLeftMargin && LeftMarginStyleName,
        ClickBustingConfig.suppressionClassName,
      )}
      labelProps={{ 'data-tb-test-id': `${testId}-Label`, ...EventUtilsOptOut } as LabelAttrs}
    />
  );
};
SelectorShim.displayName = 'SelectorShim';

export class CheckBoxWithLabelWidget extends React.PureComponent<CheckBoxWithLabelProps & InputAttrs> {
  private handleChange: React.ChangeEventHandler<HTMLInputElement> = e => {
    // IE11 bug fix: https://github.com/facebook/react/issues/4457
    if (this.props.disabled) { return; }

    if (this.props.onChange) {
      this.props.onChange(e);
    } else {
      this.props.handleChange?.(e.target.checked);
    }
  }

  public render(): JSX.Element {
    const { containerStyle, handleChange, label, ...selectorProps } = this.props;
    return (
      <SelectorShim
        {...selectorProps}
        children={label || selectorProps.children}
        onChange={this.handleChange}
        style={containerStyle || selectorProps.style}

        componentType={Checkbox}
        data-tb-test-id={`${selectorProps.testId}-CheckBox`}
      />
    );
  }
}

export class CheckBoxWidget extends React.PureComponent<CheckBoxProps & InputAttrs> {
  public render(): JSX.Element {
    // tslint:disable-next-line:deprecation
    const { hover, ...checkboxProps } = this.props;
    return <CheckBoxWithLabelWidget {...checkboxProps} label='' />;
  }
}

export class RadioButtonWithLabelWidget<T = string> extends React.PureComponent<RadioButtonWithLabelProps<T>> {
  private handleChange: React.ChangeEventHandler<HTMLInputElement> = () => {
    // IE11 bug fix: https://github.com/facebook/react/issues/4457
    if (this.props.disabled || !this.props.handleChange || this.props.checked) { return; }
    this.props.handleChange(this.props.value, this.props.index);
  }

  public render(): JSX.Element {
    const { containerStyle, groupName, handleChange, index, label, value, ...selectorProps } = this.props;
    return (
      <SelectorShim
        {...selectorProps}
        children={selectorProps.children || label}
        onChange={this.handleChange}
        style={containerStyle}

        componentType={Radio}
        data-tb-test-id={`${selectorProps.testId}-RadioButton`}
        name={groupName}
        // tslint:disable-next-line:no-any
        value={value as any}
      />
    );
  }
}

export class RadioButtonWidget<T = string> extends React.PureComponent<RadioButtonProps<T>> {
  public render(): JSX.Element {
    return <RadioButtonWithLabelWidget {...this.props} label='' />;
  }
}
